import { getCurrentStageForWorld } from "../../../utils/selectors";

import classNames from "classnames";
import React, { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "reactstrap";
import { useEditorSelector } from "../../../../hooks/redux";
import {
  Characters,
  MathOperation,
  RecordingState,
  RuleAction,
  RuleActionAnimationStyle,
  RuleValue,
} from "../../../../types";
import { updateRecordingActions } from "../../../actions/recording-actions";
import { changeActors } from "../../../actions/stage-actions";
import { selectToolId } from "../../../actions/ui-actions";
import { TOOLS } from "../../../constants/constants";
import { ruleValueFromDragPayload } from "../../../utils/stage-helpers";
import { playSound } from "../../../utils/sound-player";
import { deepClone } from "../../../utils/utils";
import { TransformEditorModal } from "../../inspector/transform-editor";
import { RELATIVE_TRANSFORMS } from "../../inspector/transform-lookup";
import { ActorDeltaCanvas } from "./actor-delta-canvas";
import { ActorOffsetCanvas } from "./actor-offset-canvas";
import { ActorBlock, VariableBlock, VariableRuleValue } from "./blocks";
import { RuleActorsContext } from "./rule-actors";
import { BackgroundConditionValue, FreeformConditionValue } from "./condition-rows";
import { TransformActionPicker } from "./transform-action-picker";
import { PickedSound, SoundPickerModal } from "./sound-picker-modal";
import { getAfterWorldForRecording } from "./utils";
import { VariableActionPicker } from "./variable-action-picker";

/**
 * Builds the action that modifies `target`, carrying over the operation and value
 * of the action it replaces (or defaults, when a variable is dropped on the panel
 * to create a new action).
 */
const ruleActionForVariable = (
  target: VariableRuleValue,
  from: {
    operation: MathOperation;
    value: RuleValue;
    animationStyle?: RuleActionAnimationStyle;
  },
): RuleAction => {
  // Only these three carry over: `from` is often the whole action being replaced,
  // and spreading it would restore the type and target we're changing.
  const { operation, value, animationStyle } = from;

  if ("stageVariableId" in target) {
    return {
      type: "stageVariable",
      stageVariable: target.stageVariableId,
      operation,
      value,
      animationStyle,
    };
  }
  if ("globalId" in target) {
    return { type: "global", global: target.globalId, operation, value, animationStyle };
  }
  if (target.variableId === "appearance") {
    return { type: "appearance", actorId: target.actorId, value, animationStyle };
  }
  return {
    type: "variable",
    actorId: target.actorId,
    variable: target.variableId,
    operation,
    value,
    animationStyle,
  };
};

export const RecordingActions = (props: { characters: Characters; recording: RecordingState }) => {
  const { characters, recording } = props;
  const { beforeWorld, actions, extent } = recording;
  const selectedToolId = useEditorSelector((state) => state.ui.selectedToolId);

  const dispatch = useDispatch();

  const beforeStage = getCurrentStageForWorld(beforeWorld);
  let afterStage = deepClone(beforeStage);

  const _renderAction = (a: RuleAction, onChange: (a: RuleAction) => void) => {
    if (!beforeStage || !afterStage || !beforeStage.actors[recording.actorId!]) {
      return;
    }
    // In a saved rule the main actor is at 0,0, but when recording on the stage
    // the extent and the position are relative to the "current" game world.
    const mainActorBeforePosition = beforeStage.actors[recording.actorId!].position;
    if ("actorId" in a && a.actorId) {
      if (a.type === "create") {
        return (
          <>
            Create a
            <ActorBlock actor={a.actor} character={characters[a.actor.characterId]} />
            at
            <ActorOffsetCanvas
              actor={a.actor}
              character={characters[a.actor.characterId]}
              extent={extent}
              offset={{
                // Express the action target in the canvas' 1-indexed
                // extent-relative frame: leftmost column = 1, bottom row = 1.
                x: a.offset!.x + mainActorBeforePosition.x - extent.xmin + 1,
                y: a.offset!.y + mainActorBeforePosition.y - extent.ymin + 1,
              }}
            />
          </>
        );
      }
      const actor = afterStage.actors[a.actorId];
      const character = characters[actor.characterId];

      if (a.type === "move") {
        return (
          <>
            Move
            <ActorBlock actor={actor} character={character} />
            to
            {a.delta ? (
              <ActorDeltaCanvas delta={a.delta} />
            ) : (
              <ActorOffsetCanvas
                actor={actor}
                character={character}
                extent={extent}
                offset={{
                  // Express the action target in the canvas' 1-indexed
                  // extent-relative frame: leftmost column = 1, bottom row = 1.
                  x: a.offset!.x + mainActorBeforePosition.x - extent.xmin + 1,
                  y: a.offset!.y + mainActorBeforePosition.y - extent.ymin + 1,
                }}
              />
            )}
          </>
        );
      }
      if (a.type === "delete") {
        return (
          <>
            Remove
            <ActorBlock actor={actor} character={character} />
            from the stage
          </>
        );
      }
      if (a.type === "teleport") {
        const door = beforeStage.actors[a.doorActorId];
        const doorCharacter = door ? characters[door.characterId] : undefined;
        return (
          <>
            Teleport
            <ActorBlock actor={actor} character={character} />
            through
            {door && doorCharacter ? (
              <ActorBlock actor={door} character={doorCharacter} />
            ) : (
              <code>door</code>
            )}
          </>
        );
      }

      if (a.type === "variable") {
        return (
          <>
            <VariableActionPicker
              operation={a.operation}
              onChangeOperation={(operation) => onChange({ ...a, operation })}
            />
            <FreeformConditionValue
              value={a.value}
              world={beforeWorld}
              actors={afterStage.actors}
              characters={characters}
              onChange={(value) => onChange({ ...a, value })}
              impliedDatatype={null}
              comparator="="
            />
            {{ set: "into", add: "to", subtract: "from" }[a.operation]}
            <VariableBlock
              value={{ actorId: a.actorId, variableId: a.variable }}
              world={beforeWorld}
              actors={afterStage.actors}
              characters={characters}
              onChange={(target) => onChange(ruleActionForVariable(target, a))}
            />
          </>
        );
      }
      if (a.type === "appearance") {
        return (
          <>
            Change appearance of
            <ActorBlock character={character} actor={actor} />
            to
            <FreeformConditionValue
              value={a.value}
              world={beforeWorld}
              actors={afterStage.actors}
              characters={characters}
              onChange={(value) => onChange({ ...a, value })}
              impliedDatatype={{ type: "appearance", character }}
              comparator="="
            />
          </>
        );
      }
      if (a.type === "transform") {
        return (
          <>
            Turn
            <ActorBlock character={character} actor={actor} />
            <TransformActionPicker
              operation={a.operation}
              onChangeOperation={(operation) => {
                if (operation === a.operation) {
                  return;
                }
                let value: RuleValue = a.value;
                if (a.value && "constant" in a.value) {
                  const v = a.value.constant;
                  if (operation === "add") {
                    const table = RELATIVE_TRANSFORMS[actor.transform ?? "0"];
                    value = { constant: Object.entries(table).find((p) => p[0] === v)![1] };
                  }
                  if (operation === "set") {
                    const table = RELATIVE_TRANSFORMS[actor.transform ?? "0"];
                    value = { constant: Object.entries(table).find((p) => p[1] === v)![0] };
                  }
                }
                onChange({ ...a, operation, value });
              }}
            />
            <FreeformConditionValue
              value={a.value}
              world={beforeWorld}
              actors={afterStage.actors}
              characters={characters}
              onChange={(value) => onChange({ ...a, value })}
              comparator="="
              impliedDatatype={{
                type: "transform",
                characterId: actor.characterId,
                appearance: actor.appearance,
              }}
            />
          </>
        );
      }
    }

    if (a.type === "global") {
      const declaration = beforeWorld.globals[a.global];

      if ("type" in declaration && declaration.type === "stage" && "constant" in a.value) {
        return (
          <>
            Set
            <VariableBlock
              value={{ globalId: a.global }}
              world={beforeWorld}
              actors={afterStage.actors}
              characters={characters}
              onChange={(target) => onChange(ruleActionForVariable(target, a))}
            />
            to
            <code>
              {beforeWorld.stages[a.value.constant] && beforeWorld.stages[a.value.constant].name}
            </code>
          </>
        );
      }
      return (
        <>
          <VariableActionPicker
            operation={a.operation}
            onChangeOperation={(operation) => onChange({ ...a, operation })}
          />
          <FreeformConditionValue
            value={a.value}
            world={beforeWorld}
            actors={afterStage.actors}
            characters={characters}
            onChange={(value) => onChange({ ...a, value })}
            impliedDatatype={null}
            comparator="="
          />
          {{ set: "into", add: "to", subtract: "from" }[a.operation]}

          <VariableBlock
            value={{ globalId: a.global }}
            world={beforeWorld}
            actors={afterStage.actors}
            characters={characters}
            onChange={(target) => onChange(ruleActionForVariable(target, a))}
          />
        </>
      );
    }

    if (a.type === "stageVariable") {
      const declaration = beforeWorld.stageVariables?.[a.stageVariable];
      // Background-typed stage variables get a preview + Set… button instead
      // of the freeform text editor. "set" is the only sensible operation.
      if (declaration && "type" in declaration && declaration.type === "background") {
        return (
          <>
            Set
            <VariableBlock
              value={{ stageVariableId: a.stageVariable }}
              world={beforeWorld}
              actors={afterStage.actors}
              characters={characters}
              onChange={(target) => onChange(ruleActionForVariable(target, a))}
            />
            to
            <BackgroundConditionValue
              value={"constant" in a.value ? a.value.constant : ""}
              stageName={getCurrentStageForWorld(beforeWorld)?.name ?? ""}
              onChange={(next) => onChange({ ...a, value: { constant: next } })}
            />
            <span style={{ marginLeft: 4, color: "#777", fontSize: 12 }}>on this Level</span>
          </>
        );
      }
      return (
        <>
          <VariableActionPicker
            operation={a.operation}
            onChangeOperation={(operation) => onChange({ ...a, operation })}
          />
          <FreeformConditionValue
            value={a.value}
            world={beforeWorld}
            actors={afterStage.actors}
            characters={characters}
            onChange={(value) => onChange({ ...a, value })}
            impliedDatatype={null}
            comparator="="
          />
          {{ set: "into", add: "to", subtract: "from" }[a.operation]}

          <VariableBlock
            value={{ stageVariableId: a.stageVariable }}
            world={beforeWorld}
            actors={afterStage.actors}
            characters={characters}
            onChange={(target) => onChange(ruleActionForVariable(target, a))}
          />
          <span style={{ marginLeft: 4, color: "#777", fontSize: 12 }}>on this Level</span>
        </>
      );
    }

    if (a.type === "sound") {
      return (
        <>
          Play sound
          <SoundActionValue
            value={{ url: a.url, name: a.name }}
            onChange={(sound) => onChange({ ...a, ...sound })}
          />
        </>
      );
    }

    throw new Error(`Unknown action type: ${(a as RuleAction).type}`);
  };

  // Actors this rule creates are numbered after the ones already in it, so
  // "Create a Coach" next to two existing Coaches reads "Coach 3" everywhere.
  const ruleActors = useContext(RuleActorsContext);
  const labelActors = ruleActors && [
    ...ruleActors,
    ...(actions || []).flatMap((action) => (action.type === "create" ? [action.actor] : [])),
  ];

  const [droppingValue, setDroppingValue] = useState(false);
  const [showAnimationFrames, setShowAnimationFrames] = useState(() =>
    actions?.some((a) => a.animationStyle),
  );

  if (!actions) {
    return <span />;
  }

  const onDropValue = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes("variable")) {
      const payload = e.dataTransfer.getData("variable");
      const target = ruleValueFromDragPayload(payload);
      const { value: constant } = JSON.parse(payload);

      if (target && !("constant" in target)) {
        const newAction = ruleActionForVariable(target, {
          operation: "set",
          value: { constant },
        });
        dispatch(updateRecordingActions([...actions, newAction]));
      }
      e.stopPropagation();
    }
    setDroppingValue(false);
  };

  const onRemoveAction = (a: RuleAction) => {
    dispatch(updateRecordingActions(actions.filter((aa) => aa !== a)));
  };

  return (
    <RuleActorsContext.Provider value={labelActors}>
      <div
        className={`panel-actions dropping-${droppingValue}`}
        style={{ flex: 1, marginLeft: 3, position: "relative" }}
        tabIndex={0}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes(`variable`)) {
            setDroppingValue(true);
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onDragLeave={() => {
          setDroppingValue(false);
        }}
        onDrop={onDropValue}
      >
        <StageAfterTools />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <h2>It should...</h2>
          <Button
            size="xs"
            style={{ padding: 4 }}
            title="Toggle visibility of animation frames"
            className={showAnimationFrames ? "selected" : ""}
            onClick={() => setShowAnimationFrames(!showAnimationFrames)}
          >
            <img
              style={{ width: 28 }}
              src={new URL("../../../img/animation-frames.svg", import.meta.url).href}
            />
          </Button>
        </div>

        <ul>
          {actions.map((a, idx) => {
            const afterWorld = getAfterWorldForRecording(beforeWorld, characters, recording, idx);
            afterStage = getCurrentStageForWorld(afterWorld);

            const node = _renderAction(a, (modified) => {
              dispatch(updateRecordingActions(actions.map((a, i) => (i === idx ? modified : a))));
            });

            return (
              <React.Fragment key={idx}>
                <li
                  className={`tool-supported`}
                  onClick={(e) => {
                    if (selectedToolId === TOOLS.TRASH) {
                      onRemoveAction(a);
                      if (!e.shiftKey) {
                        dispatch(selectToolId(TOOLS.POINTER));
                      }
                    }
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 2 }}>{node}</div>
                  <div style={{ flex: 1 }} />
                  {showAnimationFrames ? (
                    <div
                      style={{ width: 60 }}
                      className={`frame-divider ${a.animationStyle}`}
                      onClick={() => {
                        dispatch(
                          updateRecordingActions(
                            actions.map((a, i) =>
                              i === idx
                                ? {
                                    ...a,
                                    animationStyle:
                                      a.animationStyle === "none"
                                        ? "skip"
                                        : a.animationStyle === "skip"
                                          ? undefined
                                          : "none",
                                  }
                                : a,
                            ),
                          ),
                        );
                      }}
                    >
                      {
                        { none: "None", skip: "Skip", linear: "Animate" }[
                          a.animationStyle ?? "linear"
                        ]
                      }
                    </div>
                  ) : undefined}
                  <div onClick={() => onRemoveAction(a)} className="condition-remove">
                    <div />
                  </div>
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      </div>
    </RuleActorsContext.Provider>
  );
};

const SoundActionValue = ({
  value,
  onChange,
}: {
  value: PickedSound;
  onChange: (sound: PickedSound) => void;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <code style={{ cursor: "pointer" }} title="Preview" onClick={() => playSound(value.url)}>
        <i className="fa fa-volume-up" style={{ marginRight: 4 }} />
        {value.name}
      </code>
      <Button size="xs" style={{ marginLeft: 4 }} onClick={() => setOpen(true)}>
        Change…
      </Button>
      <SoundPickerModal
        open={open}
        value={value}
        onCancel={() => setOpen(false)}
        onPick={(sound) => {
          setOpen(false);
          onChange(sound);
        }}
      />
    </>
  );
};

const StageAfterTools = () => {
  const [open, setOpen] = useState(false);
  const [soundOpen, setSoundOpen] = useState(false);
  const dispatch = useDispatch();

  const characters = useEditorSelector((state) => state.characters);
  const selectedActors = useEditorSelector((state) => state.ui.selectedActors);
  const recording = useEditorSelector((state) => state.recording);
  const afterStage = getCurrentStageForWorld(
    getAfterWorldForRecording(recording.beforeWorld, characters, recording),
  );
  const actorId = selectedActors?.actorIds[0];
  const actor = actorId && afterStage?.actors[actorId];

  const enabled = actor && selectedActors.worldId === "after";

  return (
    <div
      className="floating-controls"
      style={{
        left: 0,
        zIndex: 101,
        position: "absolute",
        transform: "translate(0, -100%)",
        paddingBottom: 5,
        display: "flex",
        flexDirection: "row",
        gap: 4,
      }}
    >
      {actor && (
        <TransformEditorModal
          open={open}
          characterId={actor.characterId}
          appearance={actor.appearance}
          value={actor.transform ?? "0"}
          onChange={(transform) => {
            setOpen(false);
            dispatch(changeActors(selectedActors!, { transform }));
          }}
        />
      )}
      <Button disabled={!enabled} className={classNames({ enabled })} onClick={() => setOpen(true)}>
        Turn
      </Button>
      <SoundPickerModal
        open={soundOpen}
        value={null}
        onCancel={() => setSoundOpen(false)}
        onPick={(sound) => {
          setSoundOpen(false);
          dispatch(
            updateRecordingActions([...(recording.actions ?? []), { type: "sound", ...sound }]),
          );
        }}
      />
      <Button
        className="enabled"
        title="Play a sound"
        aria-label="Play a sound"
        onClick={() => setSoundOpen(true)}
      >
        <i className="fa fa-volume-up" />
      </Button>
    </div>
  );
};
